import { supabase } from '@/lib/supabase';
import { Lead, LeadStats, LeadFilters } from '@/features/leads/types';

// UUID validation helper
const isValidUUID = (uuid: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

export const crmService = {
  // Get all leads for a project
  async getLeads(
    projectId: string,
    filters?: LeadFilters
  ): Promise<{ data: Lead[] | null; error: Error | null }> {
    try {
      if (!isValidUUID(projectId)) {
        return { 
          data: null, 
          error: new Error(`Invalid project ID: ${projectId}. Expected valid UUID format.`) 
        };
      }

      let query = supabase
        .from('leads')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters?.search) {
        // Search in email or inside the data JSONB column
        query = query.or(`email.ilike.%${filters.search}%,data->>firstName.ilike.%${filters.search}%,data->>lastName.ilike.%${filters.search}%`);
      }

      if (filters?.startDate) {
        query = query.gte('created_at', filters.startDate);
      }

      if (filters?.endDate) {
        query = query.lte('created_at', filters.endDate);
      }

      if (filters?.source) {
        query = query.eq('source', filters.source);
      }

      const { data, error } = await query;

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  async getLeadsForProjects(
    projectIds: string[],
    filters?: LeadFilters
  ): Promise<{ data: Lead[] | null; error: Error | null }> {
    try {
      const validProjectIds = projectIds.filter(isValidUUID);
      if (validProjectIds.length === 0) {
        return { data: [], error: null };
      }

      let query = supabase
        .from('leads')
        .select('*')
        .in('project_id', validProjectIds)
        .order('created_at', { ascending: false });

      if (filters?.search) {
        query = query.or(`email.ilike.%${filters.search}%,data->>firstName.ilike.%${filters.search}%,data->>lastName.ilike.%${filters.search}%`);
      }

      if (filters?.startDate) {
        query = query.gte('created_at', filters.startDate);
      }

      if (filters?.endDate) {
        query = query.lte('created_at', filters.endDate);
      }

      if (filters?.source) {
        query = query.eq('source', filters.source);
      }

      const { data, error } = await query;
      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Get lead statistics
  async getStats(
    projectId: string
  ): Promise<{ data: LeadStats | null; error: Error | null }> {
    try {
      if (!isValidUUID(projectId)) {
        return { 
          data: null, 
          error: new Error(`Invalid project ID: ${projectId}. Expected valid UUID format.`) 
        };
      }

      // Use the new SQL function for leads
      const { data, error } = await supabase.rpc('get_project_lead_stats', {
        pid: projectId,
      });

      if (error) throw error;

      // Map the array result to single object
      return { data: data?.[0] || null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Create a new lead (public form submission)
  async createLead(
    projectId: string,
    email: string,
    data: {
      firstName?: string;
      lastName?: string;
      phone?: string;
      customAnswer?: string;
      referralCode?: string;
      referredBy?: string;
    }
  ): Promise<{ data: Lead | null; error: Error | null }> {
    try {
      if (!isValidUUID(projectId)) {
        return {
          data: null,
          error: new Error(`Invalid project ID: ${projectId}. Expected valid UUID format.`)
        };
      }

      // Check if email already exists for this project to prevent duplicates
      const { data: existing } = await supabase
        .from('leads')
        .select('id')
        .eq('project_id', projectId)
        .eq('email', email)
        .single();
        
      if (existing) {
          return { data: null, error: new Error('You are already on the waitlist!') };
      }

      // Insert into leads table
      // Note: We do NOT select back to avoid RLS issues for public users
      const { error } = await supabase
        .from('leads')
        .insert({
          project_id: projectId,
          email,
          data: data, // Store partial data as JSONB
          source: 'landing_page', 
          status: 'new'
        });

      if (error) throw error;

      // Mock response to satisfy return type
      return { 
        data: {
          id: 'submitted',
          project_id: projectId,
          email,
          data,
          source: 'landing_page',
          status: 'new',
          created_at: new Date().toISOString()
        }, 
        error: null 
      };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Delete a lead
  async deleteLead(id: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', id);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // Bulk delete leads
  async bulkDeleteLeads(ids: string[]): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('leads')
        .delete()
        .in('id', ids);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // Get dynamic queue position for a lead based on referrals
  async getLeadPosition(
    projectId: string,
    referralCode: string
  ): Promise<{ data: { queue_position: number; referral_count: number; total_leads: number } | null; error: Error | null }> {
    try {
      if (!isValidUUID(projectId)) {
        return {
          data: null,
          error: new Error(`Invalid project ID: ${projectId}. Expected valid UUID format.`)
        };
      }

      const { data, error } = await supabase.rpc('get_lead_position', {
        p_project_id: projectId,
        p_referral_code: referralCode
      });

      if (error) throw error;

      // RPC returns array, get first result
      return { data: data?.[0] || null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }
};
